import { useStore } from '@tanstack/react-form'
import z from 'zod'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { Label } from '@/frontend/components/ui/label'
import { PatientSearch } from '@/frontend/features/patient/patient-search'
import { useCreateConsultation } from '@/frontend/hooks/api/use-create-consultation'

// Intercom URL formats:
//   inbox:  .../conversation/[id]
//   app:    .../conversations/[id]
const INTERCOM_URL_RE = /\/conversation(?:s)?\/(\d+)/

const remoteConsultationSchema = z.object({
  patientId: z.uuid(),
  reason: z.string(),
  intercomUrl: z.string(),
})

type RemoteConsultationFormProps = {
  onSuccess?: () => void
  onCancel?: () => void
}

/**
 * Form for creating a new pending remote consultation request.
 * Mounted inside AddConsultationDialog on the remote consultations page.
 */
export function RemoteConsultationForm({ onSuccess, onCancel }: RemoteConsultationFormProps) {
  const { mutateAsync: createConsultation } = useCreateConsultation()

  const form = useAppForm({
    defaultValues: {
      patientId: '' as string,
      reason: '',
      intercomUrl: '',
    },
    validators: {
      onSubmit: remoteConsultationSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        const intercomConversationId = value.intercomUrl
          ? (INTERCOM_URL_RE.exec(value.intercomUrl)?.[1] ?? undefined)
          : undefined

        await createConsultation({
          patientId: value.patientId,
          reason: value.reason || undefined,
          intercomConversationId,
        })

        form.reset()
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to create consultation request',
            fields: {},
          },
        })
      }
    },
  })

  // Watch intercomUrl to show inline hint when the URL can't be parsed.
  const intercomUrl = useStore(form.store, (s) => s.values.intercomUrl)
  const intercomUrlInvalid = !!intercomUrl && !INTERCOM_URL_RE.test(intercomUrl)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 pt-2">
          <form.GlobalError />

          <form.Field name="patientId">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="patient">Patient</Label>
                <PatientSearch
                  id="patient"
                  onChange={(v) => field.handleChange(v ?? '')}
                  showClear
                />
              </div>
            )}
          </form.Field>

          <form.AppField name="reason">
            {(field) => (
              <field.TextareaField
                label="Reason (optional)"
                placeholder="Describe the reason for this consultation..."
                rows={3}
              />
            )}
          </form.AppField>

          <form.AppField name="intercomUrl">
            {(field) => (
              <>
                <field.InputField
                  label="Intercom conversation URL (optional)"
                  placeholder="https://app.intercom.com/a/inbox/.../conversation/..."
                />
                {intercomUrlInvalid && (
                  <p className="-mt-2 text-sm text-destructive-fg">
                    Could not extract a conversation ID from this URL.
                  </p>
                )}
              </>
            )}
          </form.AppField>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          {onCancel && (
            <Button type="button" variant="outline" size="lg" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton>Create Request</form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}
